import { useContext, useEffect, useMemo, useRef } from 'react'
import { createBrowserRouter, createRoutesFromElements } from 'react-router'
import { RouterProvider } from 'react-router/dom'
import { gql } from '@apollo/client'
import { useMutation } from '@apollo/client/react'

import { globalStateContext } from 'js/lib/GlobalState'
import { useSiteStore, useViewerStore } from 'js/lib/stores'
import RootRoutes from 'js/router/RootRoutes'

import 'polyfill-crypto-methods'
import 'js/i18n/i18n'

const App = () => {
    const { site } = useSiteStore()
    const { viewer } = useViewerStore()

    const { setGlobalState } = useContext(globalStateContext)

    const userIsActive = useRef(true) // Needed to track user activity locally

    const [refreshUserLastOnline] = useMutation<any>(REFRESH_USER_LAST_ONLINE)

    const handleVisibilityChange = () => {
        setGlobalState({ windowIsActive: !document.hidden })
    }

    const mouseClass = 'mouse-user'

    const handleKeyDown = (evt: KeyboardEvent) => {
        if (evt.key === 'Tab') {
            document.body.classList.remove(mouseClass)
            window.removeEventListener('keydown', handleKeyDown)
            window.addEventListener('mousedown', handleMouseDown)
        }
    }

    const handleMouseDown = () => {
        document.body.classList.add(mouseClass)
        window.removeEventListener('mousedown', handleMouseDown)
        window.addEventListener('keydown', handleKeyDown)
    }

    const registerServiceWorker = () => {
        if ('serviceWorker' in navigator) {
            const serviceWorker = document.querySelector(
                'meta[name="service-worker-js"]',
            ) as HTMLMetaElement
            if (serviceWorker) {
                navigator.serviceWorker.register(serviceWorker.content)
            }
        }
    }

    /*
        Code for keeping track of user activity in state
    */
    let inactivityTimerId: number | null = null
    const INACTIVITY_THRESHOLD_MIN = 10
    const INACTIVITY_THRESHOLD_MS = INACTIVITY_THRESHOLD_MIN * 60 * 1000

    const setUserInactive = () => {
        // Stop the timer
        clearInactivityTimer()

        // Mark user as inactive
        userIsActive.current = false
        setGlobalState({ userIsActive: false })
    }

    const clearInactivityTimer = () => {
        if (inactivityTimerId !== null) {
            clearTimeout(inactivityTimerId)
            inactivityTimerId = null
        }
    }

    const resetInactivityTimer = () => {
        // Clear the timer if it was running already
        clearInactivityTimer()

        // Mark user as inactive after 10 minutes of inactivity
        inactivityTimerId = window.setTimeout(
            setUserInactive,
            INACTIVITY_THRESHOLD_MS,
        )
    }

    const handleInteraction = () => {
        // Only update state if user was inactive
        if (!userIsActive.current) {
            userIsActive.current = true
            setGlobalState({ userIsActive: true })
        }

        // Reset the timer on user interaction
        resetInactivityTimer()
    }

    /*
        Code for keeping track of user activity on server
    */
    let lastOnlineTimerId: number | null = null
    const LAST_ONLINE_THRESHOLD_MIN = 9
    const LAST_ONLINE_THRESHOLD_MS = LAST_ONLINE_THRESHOLD_MIN * 60 * 1000

    const refreshLastOnline = () => {
        // Refresh last online status if user is active
        if (userIsActive.current && viewer?.loggedIn) {
            refreshUserLastOnline().catch((error) => {
                console.error(error)
                throw new Error(error)
            })
        }
    }

    const clearLastOnlineTimer = () => {
        if (lastOnlineTimerId !== null) {
            clearTimeout(lastOnlineTimerId)
        }
    }

    const setLastOnlineTimer = () => {
        // Set timer to refresh last online status every 9 minutes
        lastOnlineTimerId = window.setInterval(
            refreshLastOnline,
            LAST_ONLINE_THRESHOLD_MS,
        )
    }

    useEffect(() => {
        const onWindowMouseDown = () => {
            handleMouseDown()
            handleInteraction()
        }

        window.addEventListener('visibilitychange', handleVisibilityChange)
        window.addEventListener('keypress', handleInteraction)
        window.addEventListener('mousedown', onWindowMouseDown)
        registerServiceWorker()

        // Keep track of user activity in state
        handleInteraction()

        // Submit activity to server (will set User.onlineStatus to "online")
        // After 10 minutes, User.onlineStatus will be set to "offline" automatically
        refreshLastOnline()

        // Check every 9 minutes if user is still active and submit to server again
        setLastOnlineTimer()

        return () => {
            window.removeEventListener(
                'visibilitychange',
                handleVisibilityChange,
            )
            window.removeEventListener('keypress', handleInteraction)
            window.removeEventListener('keydown', handleKeyDown)
            window.removeEventListener('mousedown', onWindowMouseDown)

            clearLastOnlineTimer()
            clearInactivityTimer()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const customContentTypesKey =
        site?.customContentTypes
            ?.map(({ key, slug }) => `${key}:${slug}`)
            .join(',') ?? ''

    const router = useMemo(
        () => createBrowserRouter(createRoutesFromElements(RootRoutes(site))),
        [site, customContentTypesKey],
    )

    if (!site) {
        return null
    }

    return <RouterProvider router={router} />
}

const REFRESH_USER_LAST_ONLINE = gql`
    mutation RefreshUserLastOnline {
        refreshUserLastOnline {
            success
        }
    }
`

export default App
